import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
  getRedirectResult
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Container,
  useMediaQuery,
  useTheme,
  Link
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ShieldIcon from '@mui/icons-material/Shield';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import './Welcome.css';

const SCREENS = [
  {
    id: 'welcome',
    title: 'Benvenuto su QuantoSpendo',
    description: 'Traccia le tue spese, a modo tuo',
    icon: <HomeIcon sx={{ fontSize: '5rem', color: '#fff', marginBottom: '20px' }} />,
    showLogo: true,
    showLogin: true,
  },
  {
    id: 'privacy',
    title: 'La Tua Privacy Prima di Tutto',
    description: 'Un\'app che rispetta la tua privacy',
    icon: <ShieldIcon sx={{ fontSize: '5rem', color: '#fff', marginBottom: '20px' }} />,
    showLogo: false,
    showLogin: false,
    details: [
      'Nessun accesso a carte di credito o conti bancari',
      'Inserimento manuale delle spese per il massimo controllo',
      'I tuoi dati rimangono solo tuoi',
      'Un taccuino digitale sicuro e privato'
    ]
  },
  {
    id: 'features',
    title: 'Traccia le tue spese',
    description: 'Organizza le tue spese con categorie personalizzate e semplici da gestire.',
    icon: <CategoryIcon sx={{ fontSize: '5rem', color: '#fff', marginBottom: '20px' }} />,
    showLogo: false,
    showLogin: false,
  },
  {
    id: 'statistics',
    title: 'Esplora le Statistiche',
    description: 'Visualizza grafici dettagliati per comprendere le tue abitudini di spesa.',
    icon: <BarChartIcon sx={{ fontSize: '5rem', color: '#fff', marginBottom: '20px' }} />,
    showLogo: false,
    showLogin: false,
  },
  {
    id: 'family',
    title: 'Gestione Familiare',
    description: 'Collabora con la tua famiglia per gestire le spese in modo trasparente.',
    icon: <PeopleIcon sx={{ fontSize: '5rem', color: '#fff', marginBottom: '20px' }} />,
    showLogo: false,
    showLogin: false,
  },
];

const Welcome = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [authChecked, setAuthChecked] = useState(false);

  // Gestione completa dello stato di autenticazione
  const handleAuthState = async (user) => {
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          await setDoc(userDocRef, {
            name: user.displayName || '',
            email: user.email,
            photoURL: user.photoURL || '',
            lastLogin: new Date(),
            acceptedTerms: false,
          });
          localStorage.setItem('tempUser', JSON.stringify(user));
          return navigate('/terms-and-conditions');
        }

        const userData = userDoc.data();
        localStorage.setItem('currentUser', JSON.stringify(userData));
        
        if (userData.acceptedTerms) {
          navigate('/dashboard');
        } else {
          navigate('/terms-and-conditions');
        }
      } catch (error) {
        console.error('Errore durante il controllo dei termini:', error);
        setIsLoading(false);
      }
    }
    setAuthChecked(true);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(handleAuthState);

    // Gestione del redirect result
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          await handleAuthState(result.user);
        }
      } catch (error) {
        console.error('Errore durante il redirect:', error);
        setIsLoading(false);
      }
    };

    handleRedirectResult();
    return () => unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      await setPersistence(auth, browserLocalPersistence);
      
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account' // Forza la selezione account
      });

      if (isMobile && typeof window !== 'undefined' && window.navigator) {
        if (window.navigator.standalone) {
          await signInWithRedirect(auth, provider);
        } else {
          await signInWithPopup(auth, provider);
        }
      } else {
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      console.error('Errore login Google:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        alert('La finestra di login è stata chiusa prematuramente');
      }
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    try {
      setIsLoading(true);
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Errore login email:', error);
      setEmailError('Credenziali non valide o problema di connessione');
      setIsLoading(false);
    }
  };

  return (
    <div
      className="welcome-container"
      style={{
        backgroundImage: `url(${isMobile ? process.env.PUBLIC_URL + '/backgroundmobile.webp' : process.env.PUBLIC_URL + '/backgroundHD.webp'})`,
        position: 'relative',
        minHeight: '100vh'
      }}
    >
      {isLoading && (
        <div className="overlay">
          <CircularProgress size={60} sx={{ color: '#fff' }} />
          <Typography variant="h6" sx={{ color: '#fff', mt: 2 }}>
            Autenticazione in corso...
          </Typography>
        </div>
      )}

      {SCREENS.map((screen) => (
        <Box
          key={screen.id}
          id={screen.id}
          className="screen"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: isMobile ? '20px' : '40px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            width: '100%',
          }}
        >
          {screen.showLogo && (
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="QuantoSpendo Logo"
              className="logo"
            />
          )}
          {screen.icon}
          <Container maxWidth="sm">
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontSize: isMobile ? '1.8rem' : '2.5rem',
                fontWeight: 'bold',
                marginBottom: '1rem',
              }}
            >
              {screen.title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#fff',
                fontSize: isMobile ? '1rem' : '1.2rem',
                marginBottom: screen.details ? '1rem' : '2rem',
              }}
            >
              {screen.description}
            </Typography>
            {screen.details && (
              <Box sx={{ mt: 2, mb: 4 }}>
                {screen.details.map((detail, idx) => (
                  <Typography
                    key={idx}
                    sx={{
                      color: '#fff',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      padding: '12px',
                      marginBottom: '8px',
                      borderRadius: '8px',
                      textAlign: 'left',
                    }}
                  >
                    • {detail}
                  </Typography>
                ))}
              </Box>
            )}
          </Container>

          {screen.showLogin && (
            <>
              <Box className="login-button-container">
                <Button
                  variant="contained"
                  onClick={() => {
                    localStorage.removeItem('firebase:authUser');
                    handleGoogleLogin();
                  }}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: '#fff',
                    color: '#333',
                    padding: '12px 24px',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    borderRadius: '50px',
                    textTransform: 'none',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: '#f8f8f8',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                    },
                  }}
                  className="login-button"
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Accedi con Google'}
                </Button>
              </Box>
              <Box mt={2}>
                <Typography variant="body2" align="center" sx={{ color: '#fff' }}>
                  Non hai un account Google?{' '}
                  <a
                    href="https://accounts.google.com/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#fff', textDecoration: 'underline' }}
                  >
                    Crealo
                  </a>
                </Typography>
              </Box>
              <Box
                mt={3}
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  mx: 'auto',
                  backgroundColor: '#fff',
                  padding: '16px',
                  borderRadius: '8px',
                }}
              >
                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  variant="outlined"
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {emailError && (
                  <Typography variant="body2" color="error">
                    {emailError}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleEmailLogin}
                  disabled={isLoading}
                  sx={{
                    mt: 2,
                    padding: '12px 0',
                    textTransform: 'none',
                    fontSize: '1rem',
                  }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Accedi con Email'}
                </Button>
                <Box mt={2}>
                  <Typography variant="body2" align="center">
                    Non hai un account?{' '}
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => navigate('/register')}
                      sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                      Registrati
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      ))}

      <Box sx={{ 
        p: 3, 
        textAlign: 'center', 
        bgcolor: 'rgba(0, 0, 0, 0.7)', 
        position: 'fixed', 
        bottom: 0, 
        width: '100%', 
        borderTop: '1px solid #fff',
        zIndex: 1000
      }}>
        <Typography variant="body2" sx={{ color: '#fff' }}>
          © {new Date().getFullYear()} QuantoSpendo
        </Typography>
        <Typography variant="body2" sx={{ color: '#fff' }}>
          <Link
            href="https://www.iubenda.com/privacy-policy/93756875"
            className="iubenda-white iubenda-noiframe iubenda-embed"
            title="Privacy Policy"
            sx={{ color: '#fff', textDecoration: 'underline', '&:hover': { color: "primary.main" } }}
          >
            Privacy Policy
          </Link>
          {' | '}
          <Link
            href="https://www.iubenda.com/privacy-policy/93756875/cookie-policy"
            className="iubenda-white iubenda-noiframe iubenda-embed"
            title="Cookie Policy"
            sx={{ color: '#fff', textDecoration: 'underline', '&:hover': { color: "primary.main" } }}
          >
            Cookie Policy
          </Link>
        </Typography>
      </Box>
    </div>
  );
};

export default Welcome;